// ✅ src/pages/VehicleV2/AddTyreListing.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/addTyreListing", addTyreListing);
//
// API used:
//   GET  http://nissi-india.com/api/addTyreListing/list?type=N|R&branchId=..&page=1&limit=10&tyreSerialNo=&tyreSize=&vehicleName=&vehicleRegNo=&vehicleCode=&search=
//   POST http://nissi-india.com/api/addTyreListing/delete   body: { ids:[...], type, loginUserId }
//
// ✅ Changes based on your screenshot request:
// - Filter form redesigned to match ASPX (2 columns, labels left, inputs right)
// - Search + View All buttons centered (ASPX style)
// - Scrollbar ONLY inside table content (not whole page)
// - Sticky table header while scrolling

import { useState, useContext, useEffect, useMemo } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaEye } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/addTyreListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const AddTyreListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchId = Number(localStorage.getItem("branchid") || 8);

  // ✅ New / Retread toggle (like ASPX radio)
  const [tyreType, setTyreType] = useState("N"); // N=new, R=retread

  // filters (match ASPX fields)
  const [tyreSerialNo, setTyreSerialNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");

  // optional global search (kept but not shown in form to match ASPX)
  const [search, setSearch] = useState("");

  const [showFilters, setShowFilters] = useState(true);

  // data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // paging
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // loading
  const [loading, setLoading] = useState(false);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const fetchList = async (pageNo = page, lim = limit) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          type: tyreType,
          branchId,
          page: pageNo,
          limit: lim,
          tyreSerialNo,
          tyreSize,
          vehicleName,
          vehicleRegNo,
          vehicleCode,
          search,
        },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        setRows([]);
        setTotal(0);
        setPages(1);
        return;
      }

      setRows(res.data.list || []);
      setTotal(toInt(res.data.total, 0));
      setPages(toInt(res.data.pages, 1));

      // reset selection whenever list reloads
      setSelectedIds([]);
      setSelectAll(false);
    } catch (e) {
      toast.error("Server error while loading list");
      setRows([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // type change -> reset page
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreType]);

  useEffect(() => {
    fetchList(page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, tyreType]);

  const onSearchClick = () => {
    setPage(1);
    fetchList(1, limit);
  };

  const handleClearFilters = () => {
    setTyreSerialNo("");
    setVehicleName("");
    setTyreSize("");
    setVehicleRegNo("");
    setVehicleCode("");
    setSearch("");
    setPage(1);
    setTimeout(() => fetchList(1, limit), 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    const ids = (rows || []).map((r) => r.id).filter((x) => x > 0);
    setSelectedIds(ids);
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const ids = (rows || []).map((r) => r.id).filter((x) => x > 0);
    if (ids.length === 0) {
      setSelectAll(false);
      return;
    }
    setSelectAll(ids.every((id) => selectedIds.includes(id)));
  }, [selectedIds, rows]);

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one tyre to delete!");
      return;
    }

    try {
      const res = await axios.post(`${API_BASE}/delete`, {
        ids: selectedIds,
        type: tyreType,
        loginUserId: toInt(loginUserId),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      const failed = res.data.failedIds || [];
      const deleted = res.data.deletedIds || [];

      if (failed.length > 0 && deleted.length > 0) {
        toast.info(
          `Removed ${deleted.length}. ${failed.length} could not be deleted (already used).`
        );
      } else if (failed.length > 0) {
        toast.warn("Cannot be deleted. Tyre is already used.");
      } else {
        toast.success("Deleted successfully");
      }

      fetchList(page, limit);
    } catch (e) {
      toast.error("Server error while deleting");
    }
  };

  const handleEdit = (id) => {
    navigate(`/vehicle/AddTyreEntry?tyreId=${id}&type=${tyreType}`);
  };

 const handleView = (tyre) => {
  const tyreMasterId = tyre.id;   // ✅ NewTyre_ID or RetreadedTyre_ID
  window.open(
    `/vehicle/AddTyreSLT?tyreId=${tyreMasterId}&type=${tyreType}`,
    "_blank"
  );
};


  const pageButtons = useMemo(() => {
    const maxBtns = 7;
    const p = page;
    const totalPages = pages;

    if (totalPages <= maxBtns) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const left = Math.max(1, p - 2);
    const right = Math.min(totalPages, p + 2);

    const arr = [];
    arr.push(1);

    if (left > 2) arr.push("...");

    for (let i = left; i <= right; i++) {
      if (i !== 1 && i !== totalPages) arr.push(i);
    }

    if (right < totalPages - 1) arr.push("...");

    arr.push(totalPages);
    return arr;
  }, [page, pages]);

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">ADD TYRE LISTING</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top Header row */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Add Tyre Listing
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {total} Records
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate("/vehicle/addTyreSLT")}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaPlus className="w-4 h-4" />
              Add New Tyre
            </button>
          </div>
        </div>

        {/* Type Toggle */}
        <div className="flex items-center gap-6 mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={tyreType === "N"}
              onChange={() => setTyreType("N")}
            />
            <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
              New Tyre
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              checked={tyreType === "R"}
              onChange={() => setTyreType("R")}
            />
            <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
              Retreaded Tyre
            </span>
          </label>
        </div>

        {/* ✅ ASPX-like Filter Form */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            {/* Title bar like ASPX */}
            <div className="mb-6">
              <div
                className={`h-10 flex items-center px-4 font-semibold tracking-wide ${
                  isDark ? "bg-white/10 text-white" : "bg-blue-100 text-blue-900"
                }`}
              >
                ADD TYRE LISTING
              </div>
            </div>

            {/* 2-column layout like ASPX */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
              {/* LEFT */}
              <div className="space-y-5">
                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Tyre Serial No
                  </label>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={tyreSerialNo}
                      onChange={(e) => setTyreSerialNo(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Vehicle Name
                  </label>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={vehicleName}
                      onChange={(e) => setVehicleName(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Tyre Size
                  </label>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={tyreSize}
                      onChange={(e) => setTyreSize(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="space-y-5">
                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Vehicle Reg No
                  </label>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={vehicleRegNo}
                      onChange={(e) => setVehicleRegNo(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-12 items-center gap-4">
                  <label
                    className={`col-span-4 text-sm ${
                      isDark ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Vehicle Code
                  </label>
                  <div className="col-span-8">
                    <input
                      type="text"
                      value={vehicleCode}
                      onChange={(e) => setVehicleCode(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* spacing like ASPX */}
                <div className="hidden md:block h-[52px]" />
              </div>
            </div>

            {/* Buttons centered like ASPX */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
              <button
                onClick={onSearchClick}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-10 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition`}
              >
                <FaSearch />
                Search
              </button>

              <button
                onClick={handleClearFilters}
                className={`flex items-center gap-2 px-10 py-3 rounded-lg font-semibold border shadow-sm hover:shadow transition ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-700"
                    : "border-gray-300 text-gray-800 hover:bg-gray-100"
                }`}
              >
                View All
              </button>
            </div>
          </div>
        )}

        {/* ✅ Table with scrollbar ONLY inside table content */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Tyre SerialNo
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Vehicle RegNo
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Fitting Date
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Fitting Hour KM
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Removing Date
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Removing Hour KM
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Life</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Retread / Original
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      Actions
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center">
                        Select All
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="ml-2"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan={12}
                        className={`px-4 py-10 text-center ${
                          isDark ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        className={`px-4 py-10 text-center ${
                          isDark ? "text-gray-200" : "text-gray-700"
                        }`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((tyre, idx) => (
                      <tr
                        key={tyre.fld_Tyreid || idx}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.slno ?? idx + 1 + (page - 1) * limit}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.tyreSerialNo || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.vehicleCode || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.vehicleRegNo || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.fittingDate || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.fittingHourKM || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.removingDate || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.removingHourKM || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.life || ""}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {tyre.retreadOriginal || ""}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(tyre.fld_Tyreid)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEdit />
                              Edit
                            </button>
                            <button
                              onClick={() => handleView(tyre) }  // ✅ pass full row
                              className="inline-flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEye />
                              View
                            </button>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(tyre.id)}
                            onChange={() => toggleSelect(tyre.id)}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination + Delete */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={`px-3 py-2 rounded border ${
                page <= 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : isDark
                  ? "border-gray-600 hover:bg-gray-800"
                  : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Prev
            </button>

            {pageButtons.map((p, i) =>
              p === "..." ? (
                <span key={`dots-${i}`} className="px-2">
                  ...
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-2 rounded border ${
                    page === p
                      ? `${buttonClass} text-white border-transparent`
                      : isDark
                      ? "border-gray-600 hover:bg-gray-800"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className={`px-3 py-2 rounded border ${
                page >= pages
                  ? "bg-gray-300 cursor-not-allowed"
                  : isDark
                  ? "border-gray-600 hover:bg-gray-800"
                  : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Next
            </button>

            <div className="ml-2 flex items-center gap-2">
              <span className={`${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Rows:
              </span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(toInt(e.target.value, 10));
                  setPage(1);
                }}
                className={`px-2 py-2 rounded border ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleDelete}
            className={`flex items-center justify-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
              selectedIds.length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : `${buttonClass} ${hoverClass}`
            }`}
            disabled={selectedIds.length === 0}
          >
            <FaTrash /> Delete Selected
          </button>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default AddTyreListing;
